/**
 * URL-param state for the two ICR coverage modals (P4; shared contract with P5/P10).
 *
 * Scheme (params live on the ICR route, e.g. /icr/ICR-1):
 *   ?coverage=collection|analytic     modal open + which modal
 *   &tab=<tabkey>                     active tab (validated per section, default 'overview')
 *   &eei=<id>                         collection EEI tab — expanded EEI row
 *   &q=<questionId>                   analytic — active question (Questions tab anchor)
 *   &conflict=<conflictId>            analytic — active conflict (Conflicts tab anchor)
 *
 * The serialized URL (path + search) is exactly what C2's cmViewReturn expects,
 * e.g. "/icr/ICR-1?coverage=collection&tab=eei&eei=<id>", so the viewer's back
 * button restores the full modal state.
 */

export const COVERAGE_PARAM = 'coverage';
export const COVERAGE_TAB_PARAM = 'tab';
export const COVERAGE_EEI_PARAM = 'eei';
export const COVERAGE_QUESTION_PARAM = 'q';
export const COVERAGE_CONFLICT_PARAM = 'conflict';

export const COVERAGE_SECTIONS = {
    COLLECTION: 'collection',
    ANALYTIC: 'analytic',
};

export const COLLECTION_TABS = {
    OVERVIEW: 'overview',
    EEI: 'eei',
    CITATIONS: 'citations',
};

export const ANALYTIC_TABS = {
    OVERVIEW: 'overview',
    QUESTIONS: 'questions',
    CONFLICTS: 'conflicts',
};

const VALID_TABS = {
    [COVERAGE_SECTIONS.COLLECTION]: new Set(Object.values(COLLECTION_TABS)),
    [COVERAGE_SECTIONS.ANALYTIC]: new Set(Object.values(ANALYTIC_TABS)),
};

function toParams(search) {
    return new URLSearchParams((search || '').replace(/^\?/, ''));
}

function toSearchString(params) {
    const s = params.toString();
    return s ? `?${s}` : '';
}

/**
 * @param {string} search Location search string (with or without leading '?').
 * @returns {{section, tab, eeiId, questionId, conflictId}|null} null when no modal is open.
 */
export function readCoverageModalState(search) {
    const params = toParams(search);
    const section = params.get(COVERAGE_PARAM);
    if (section !== COVERAGE_SECTIONS.COLLECTION && section !== COVERAGE_SECTIONS.ANALYTIC) {
        return null;
    }
    const rawTab = params.get(COVERAGE_TAB_PARAM);
    const tab = VALID_TABS[section].has(rawTab) ? rawTab : 'overview';
    return {
        section,
        tab,
        eeiId: section === COVERAGE_SECTIONS.COLLECTION ? (params.get(COVERAGE_EEI_PARAM) || null) : null,
        questionId: section === COVERAGE_SECTIONS.ANALYTIC ? (params.get(COVERAGE_QUESTION_PARAM) || null) : null,
        conflictId: section === COVERAGE_SECTIONS.ANALYTIC ? (params.get(COVERAGE_CONFLICT_PARAM) || null) : null,
    };
}

/**
 * Builds a search string for the given modal state, preserving unrelated params.
 * Section-irrelevant anchors are always dropped (eei for analytic; q/conflict for collection).
 */
export function buildCoverageModalSearch(currentSearch, {
    section,
    tab = 'overview',
    eeiId = null,
    questionId = null,
    conflictId = null,
} = {}) {
    const params = toParams(currentSearch);
    params.delete(COVERAGE_EEI_PARAM);
    params.delete(COVERAGE_QUESTION_PARAM);
    params.delete(COVERAGE_CONFLICT_PARAM);

    params.set(COVERAGE_PARAM, section);
    params.set(COVERAGE_TAB_PARAM, VALID_TABS[section]?.has(tab) ? tab : 'overview');
    if (section === COVERAGE_SECTIONS.COLLECTION && eeiId) {
        params.set(COVERAGE_EEI_PARAM, String(eeiId));
    }
    if (section === COVERAGE_SECTIONS.ANALYTIC) {
        if (questionId) {
            params.set(COVERAGE_QUESTION_PARAM, String(questionId));
        }
        if (conflictId) {
            params.set(COVERAGE_CONFLICT_PARAM, String(conflictId));
        }
    }
    return toSearchString(params);
}

/** Removes only the coverage-modal params from a search string. */
export function stripCoverageModalParams(search) {
    const params = toParams(search);
    params.delete(COVERAGE_PARAM);
    params.delete(COVERAGE_TAB_PARAM);
    params.delete(COVERAGE_EEI_PARAM);
    params.delete(COVERAGE_QUESTION_PARAM);
    params.delete(COVERAGE_CONFLICT_PARAM);
    return toSearchString(params);
}
